import {
  CountryCode,
  LanguageCode,
  Locale,
} from 'typescript/constants/localization';
import { getEnvironmentMetadata } from 'typescript/utilities/environment';

/**
 * This file is a migration / refactor of the original locales.js file.
 * app/javascript/utilities/locales.js
 * not EVERYTHING is migrated, only the parts that are used in the checkout_v3 app
 * migrate the rest of the file as needed.
 */

const SHOP_HARDWARE_PREFIX = '/shop/hardware';

// Locale URL segments are always two-letter codes (e.g. /us/en). Anything
// else means the URL doesn't carry a locale at that position (e.g. /login,
// or the /v2 app prefix).
const LOCALE_SEGMENT = /^[a-z]{2}$/i;

/**
 * shop URLs will look like /shop/hardware/us/en/checkout/v3 or
 * /shop/hardware/v2/us/en/... — the /v2 app prefix is stripped so the
 * locale segments are always at positions 1 and 2
 * @returns string - the locale path from the window location
 */
export const getLocaleFromPathname = (): string => {
  const pathname = window.location.pathname
    .replace(SHOP_HARDWARE_PREFIX, '')
    .replace(/^\/v2\//, '/');
  return pathname.slice(0, 6);
};

/**
 * On URLs without a locale path (e.g. /login) falls back to the server's
 * environment locale, then to en. Previously this produced invalid locales
 * like 'undefined-LOGIN' or 'us-V2' that crashed Intl.NumberFormat.
 * @returns LanguageCode - the language code from the locale path
 * e.g. /shop/hardware/us/en/checkout/v3 -> en
 */
export const getLanguageCode = (): LanguageCode => {
  const code = getLocaleFromPathname().split('/')[2];
  if (LOCALE_SEGMENT.test(code)) {
    return code as LanguageCode;
  }
  return (
    (getEnvironmentMetadata()?.lang_code as LanguageCode) || LanguageCode.EN
  );
};

/**
 * @returns CountryCode - the country code from the locale path
 * e.g. /shop/hardware/us/en/checkout/v3 -> US
 */
export const getCountryCode = (): CountryCode => {
  const code = getLocaleFromPathname().split('/')[1];
  if (LOCALE_SEGMENT.test(code)) {
    return code.toUpperCase() as CountryCode;
  }
  return (
    (getEnvironmentMetadata()?.country_code?.toUpperCase() as CountryCode) ||
    CountryCode.US
  );
};

/**
 * @returns the locale string from the locale path
 * e.g. /shop/hardware/us/en/checkout/v3 -> en-US
 */
export const getLocaleString = (): Locale => {
  const countryCode = getCountryCode();
  const languageCode = getLanguageCode();

  return `${languageCode}-${countryCode}` as Locale;
};

/**
 * @returns the locale string from the locale path but for internal usage since
 * we have a special case for Japanese language when we are using this with i18next and contentful
 * e.g. /shop/hardware/us/en/checkout/v3 -> en-US
 * e.g. /shop/hardware/jp/ja/checkout/v3 -> ja
 */
export const getLocaleStringInternal = (): string | Locale => {
  const countryCode = getCountryCode();
  const languageCode = getLanguageCode();

  return languageCode === 'ja' ? 'ja' : `${languageCode}-${countryCode}`;
};
